import React, { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { getLink } from '@helpers/env'
import trackError from '@helpers/trackError'
import useSessionStorage from '@helpers/hooks/useSessionStorage'
import Translations from '@helpers/translations'
import { returnsAnalytics } from '@components/returns/helpers'
import { ReturnsHeaderImage } from '@components/returns/ReturnsHeaderImage'
import PageLoading from '@components/Loading/PageLoading'
import Notification, { NotificationProps } from '@components/ui/Notification'
import ReturnsBanner from '../ReturnsBanner'
import DeliveryOptions from './DeliveryOptions'
import styles from '../ItemsSelection/styles.module.scss'
import { Carriers, Carrier } from '@api/returns/types'
import { EmptyObject } from 'types/helpers'
import { createReturn, getCarriersAvailable } from '@api/returns'

export const DELIVERY_METHOD_WITH_DATE_DROPDOWN = [330]

type CarrierSelectionProps = {
  orderNumber: string
  postcode: string
  returnId: string
  setLabel: (args: { encodedLabel: string; encoding: string }) => void
  headerImage: {
    desktop: string
    mobile: string
  }
}

export type CollectionDate = { collectionDate?: string }

type GetSubmitButtonStatusArgs = {
  id: number
} & CollectionDate

function getSubmitButtonStatus({
  id,
  collectionDate
}: GetSubmitButtonStatusArgs) {
  const isDeliveryMethodWithDateDropdown =
    DELIVERY_METHOD_WITH_DATE_DROPDOWN.includes(id)
  return (
    (!!id && !isDeliveryMethodWithDateDropdown) ||
    (isDeliveryMethodWithDateDropdown && !!collectionDate)
  )
}

const CarrierSelection = ({
  orderNumber,
  postcode,
  returnId,
  setLabel,
  headerImage
}: CarrierSelectionProps) => {
  const router = useRouter()
  const setHasShownReturnsNoteModal = useSessionStorage(
    'hasShownReturnsNoteModal',
    true
  )[1]
  const [deliveryMethods, setDeliveryMethods] = useState<Carriers>([])
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<
    (Carrier & CollectionDate) | EmptyObject
  >({})
  const [notificationError, setNotificationError] =
    useState<NotificationProps | null>(null)

  useEffect(() => {
    getCarriersAvailable(orderNumber)
      .then(carriers => {
        setSelectedDeliveryMethod(carriers[0])
        setDeliveryMethods(carriers)
      })
      .catch(error => {
        trackError(new Error('getCarriersAvailable failed'), { error })
        setNotificationError({
          type: 'error',
          content: (
            <p>
              {Translations.localise(
                'pages.returns.returns-notifications.return-carrier.copy-1'
              )}{' '}
              <a
                style={{ color: '#DC1E32' }}
                href={getLink('/pages/contact-us')}
                target="_blank"
                rel="noreferrer"
              >
                {Translations.localise(
                  'pages.returns.returns-notifications.return-carrier.copy-2'
                )}
              </a>{' '}
              {Translations.localise(
                'pages.returns.returns-notifications.return-carrier.copy-3'
              )}{' '}
              <a
                style={{ color: '#DC1E32' }}
                href={getLink(
                  `/pages/returns?order_number=${orderNumber}&postcode=${postcode}`
                )}
              >
                {Translations.localise(
                  'pages.returns.returns-notifications.return-carrier.copy-4'
                )}
              </a>{' '}
              {Translations.localise(
                'pages.returns.returns-notifications.return-carrier.copy-5'
              )}
            </p>
          )
        })
      })
  }, [])

  const handleCreateReturn = () => {
    createReturn({
      returnId,
      serviceId: selectedDeliveryMethod.id,
      ...(selectedDeliveryMethod?.collectionDate && {
        collectionDate: selectedDeliveryMethod.collectionDate
      })
    })
      .then(res => {
        setHasShownReturnsNoteModal(false)
        setLabel({
          encodedLabel: res.data?.label,
          encoding: res.data?.encoding
        })
        returnsAnalytics('Return Label Generated', {
          carrier: selectedDeliveryMethod?.title,
          return_description: selectedDeliveryMethod?.byline,
          return_id: returnId,
          order_id: orderNumber
        })

        const { query } = router

        router.replace(
          `/pages?pagesHandle=returns&order_number=${orderNumber}&postcode=${postcode}&successReturn=${returnId}`,
          `/pages/returns?order_number=${orderNumber}&postcode=${postcode}&successReturn=${returnId}`,
          { shallow: true }
        )
      })
      .catch(error => {
        setNotificationError({
          type: 'error',
          content: (
            <span>
              {Translations.localise(
                'pages.returns.returns-notifications.return-default.copy-1'
              )}{' '}
              <a
                style={{ color: '#DC1E32' }}
                href={getLink('/pages/contact-us')}
              >
                {Translations.localise(
                  'pages.returns.returns-notifications.return-default.copy-2'
                )}
              </a>{' '}
              {Translations.localise(
                'pages.returns.returns-notifications.return-default.copy-3'
              )}
            </span>
          )
        })
        trackError(new Error('createReturn failed'), { error })
      })
  }

  const shouldShowSubmitButton =
    !!deliveryMethods.length && !!selectedDeliveryMethod?.id

  const isSubmitButtonActive = getSubmitButtonStatus({
    id: selectedDeliveryMethod.id,
    collectionDate: selectedDeliveryMethod.collectionDate
  })
  return (
    <section className={styles.return}>
      <ReturnsHeaderImage
        content={{
          title: Translations.localise('pages.returns.select-courier'),
          byline: [`Step 2/2`],
          image: headerImage
        }}
      />
      {notificationError?.type && <Notification {...notificationError} />}
      {!deliveryMethods?.length ? (
        <PageLoading />
      ) : (
        <>
          <DeliveryOptions
            deliveryMethods={deliveryMethods}
            carrierId={selectedDeliveryMethod?.id || deliveryMethods[0].id}
            onChangeDeliveryMethod={setSelectedDeliveryMethod}
            selectedDeliveryMethod={selectedDeliveryMethod}
          />
          <ReturnsBanner
            active={shouldShowSubmitButton}
            buttonActive={isSubmitButtonActive}
            type="deliveryMethod"
            onAction={handleCreateReturn}
            copy={`${selectedDeliveryMethod?.title} ${Translations.localise(
              'pages.returns.return-selected'
            )}`}
          />
        </>
      )}
    </section>
  )
}

export default CarrierSelection
